// Copy-out: pure, unit-testable plain-text formatters for the CV Workspace's copy controls.
// VERBATIM user words only — no composition, no added phrasing beyond the separators/bullets below.

export interface CopyBullet { text: string }
export interface CopyEntry {
  title: string | null;
  organization: string | null;
  dateText: string | null;
  bullets: CopyBullet[];
}
export interface CopySection {
  name: string;
  entries: CopyEntry[];
}
export interface CopyContact { name: string; email: string; phone: string; city: string }
export interface CopyModel { contact: CopyContact; sections: CopySection[] }

// "Title, Organization — dates" + bulleted lines.
// - no org → "Title — dates"
// - no dates → "Title, Organization"
// - no title (untitled) → organization takes the lead position instead (matching the renderer's fallback,
//   which simply omits a missing title rather than inserting placeholder text)
export function formatEntry(entry: CopyEntry): string {
  const title = entry.title?.trim() || '';
  const org = entry.organization?.trim() || '';
  const dates = entry.dateText?.trim() || '';

  let lead: string;
  if (title) {
    lead = org ? `${title}, ${org}` : title;
  } else {
    lead = org; // untitled → organization as the lead
  }
  const headline = dates ? (lead ? `${lead} — ${dates}` : dates) : lead;

  const bulletLines = entry.bullets.map((b) => `• ${b.text}`);
  return [headline, ...bulletLines].filter((l) => l !== '').join('\n');
}

// "SECTION NAME (uppercase)\n" + entries joined by blank lines.
export function formatSection(section: CopySection): string {
  const heading = section.name.toUpperCase();
  const entries = section.entries.map(formatEntry).join('\n\n');
  return entries ? `${heading}\n${entries}` : heading;
}

// Contact line ("Name · city · email · phone", omitting blanks) + all sections joined by blank lines.
export function formatCv(model: CopyModel): string {
  const contactLine = [model.contact.name, model.contact.city, model.contact.email, model.contact.phone]
    .map((v) => v?.trim() || '')
    .filter((v) => v !== '')
    .join(' · ');
  const sections = model.sections.map(formatSection).join('\n\n');
  return [contactLine, sections].filter((s) => s !== '').join('\n\n');
}
